import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Package, Truck, CheckCircle, Clock, MapPin, Phone, ArrowLeft, Receipt, Star } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import ReviewModal from '../components/ReviewModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OrderDetail = () => {
  const { orderId } = useParams();
  const { token } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReview, setShowReview] = useState(false);

  useEffect(() => {
    fetchOrder();
  }, [orderId]);

  const fetchOrder = async () => {
    try {
      const response = await axios.get(`${API}/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrder(response.data);
    } catch (error) {
      console.error('Order fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusInfo = (status) => {
    switch (status) {
      case 'hazirlaniyor':
        return {
          icon: Package,
          label: 'Hazırlanıyor',
          color: 'text-amber-600',
          bgColor: 'bg-amber-50',
          step: 1
        };
      case 'yolda':
        return {
          icon: Truck,
          label: 'Yolda',
          color: 'text-blue-600',
          bgColor: 'bg-blue-50',
          step: 2
        };
      case 'teslim_edildi':
        return {
          icon: CheckCircle,
          label: 'Teslim Edildi',
          color: 'text-green-600',
          bgColor: 'bg-green-50',
          step: 3
        };
      default:
        return {
          icon: Clock,
          label: status,
          color: 'text-gray-600',
          bgColor: 'bg-gray-50',
          step: 0
        };
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-terracotta border-t-transparent" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-playfair text-2xl font-semibold text-charcoal mb-4">
            Sipariş bulunamadı
          </h2>
          <Link to="/orders">
            <Button className="bg-terracotta text-white hover:bg-terracotta/90 rounded-full">
              Siparişlerime Dön
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const statusInfo = getStatusInfo(order.status);
  const StatusIcon = statusInfo.icon;

  return (
    <div className="min-h-screen bg-bone pb-8">
      {/* Header */}
      <div className="bg-sand py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/orders" className="inline-flex items-center gap-2 text-charcoal/60 hover:text-charcoal mb-4">
            <ArrowLeft className="w-4 h-4" />
            Siparişlerime Dön
          </Link>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="font-playfair text-4xl md:text-5xl font-bold text-charcoal mb-2">
              Sipariş Detayı
            </h1>
            <p className="text-charcoal/60">
              Sipariş #{order.id.slice(-8).toUpperCase()}
            </p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Status Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl p-6 shadow-sm"
            >
              <h2 className="font-playfair text-xl font-semibold text-charcoal mb-6">
                Sipariş Durumu
              </h2>
              
              {/* Status Badge */}
              <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${statusInfo.bgColor} ${statusInfo.color} mb-6`}>
                <StatusIcon className="w-5 h-5" />
                <span className="font-semibold">{statusInfo.label}</span>
              </div>

              {/* Progress Steps */}
              <div className="relative">
                <div className="absolute top-5 left-0 right-0 h-1 bg-stone-200 rounded-full">
                  <div 
                    className="h-full bg-basil rounded-full transition-all duration-500"
                    style={{ width: `${(statusInfo.step / 3) * 100}%` }}
                  />
                </div>
                <div className="relative flex justify-between">
                  {[
                    { step: 1, label: 'Hazırlanıyor', icon: Package },
                    { step: 2, label: 'Yolda', icon: Truck },
                    { step: 3, label: 'Teslim Edildi', icon: CheckCircle }
                  ].map(({ step, label, icon: Icon }) => (
                    <div key={step} className="flex flex-col items-center">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center z-10 transition-all ${
                        statusInfo.step >= step 
                          ? 'bg-basil text-white' 
                          : 'bg-stone-200 text-stone-500'
                      }`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <span className={`text-sm mt-2 ${
                        statusInfo.step >= step ? 'text-charcoal font-medium' : 'text-charcoal/50'
                      }`}>
                        {label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Order Items */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-2xl p-6 shadow-sm"
            >
              <div className="flex items-center gap-3 mb-6">
                <Receipt className="w-5 h-5 text-terracotta" />
                <h2 className="font-playfair text-xl font-semibold text-charcoal">
                  Sipariş İçeriği
                </h2>
              </div>
              
              <div className="space-y-4">
                {order.items.map((item, index) => (
                  <div key={index} className="flex justify-between items-start py-3 border-b border-stone-100 last:border-0">
                    <div>
                      <p className="font-medium text-charcoal">
                        {item.quantity}x {item.name}
                      </p>
                      <span className="text-xs text-charcoal/50 uppercase">
                        {item.item_type === 'catering' ? 'Catering' : 'Alakart'}
                      </span>
                      {item.catering_selections && (
                        <div className="text-sm text-charcoal/60 mt-1">
                          {Object.values(item.catering_selections)
                            .filter(v => v)
                            .map(v => v.name)
                            .join(', ')}
                        </div>
                      )}
                    </div>
                    <span className="font-medium text-charcoal">
                      €{(item.price * item.quantity).toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Delivery / Channel Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-2xl p-6 shadow-sm"
            >
              {order.address ? (
                <>
                  <div className="flex items-center gap-3 mb-4">
                    <MapPin className="w-5 h-5 text-basil" />
                    <h2 className="font-playfair text-xl font-semibold text-charcoal">Teslimat Adresi</h2>
                  </div>
                  <p className="text-charcoal font-medium mb-1">{order.address.title}</p>
                  <p className="text-charcoal/70">{order.address.address}</p>
                  <p className="text-charcoal/60">{order.address.district}, {order.address.city}</p>
                </>
              ) : order.table_number ? (
                <>
                  <div className="flex items-center gap-3 mb-4">
                    <Receipt className="w-5 h-5 text-basil" />
                    <h2 className="font-playfair text-xl font-semibold text-charcoal">Masa Siparişi</h2>
                  </div>
                  <p className="text-charcoal font-medium">Masa No: <span className="font-bold">{order.table_number}</span></p>
                  {order.waiter_name && <p className="text-charcoal/60 text-sm mt-1">Garson: {order.waiter_name}</p>}
                </>
              ) : (
                <>
                  <div className="flex items-center gap-3 mb-4">
                    <Package className="w-5 h-5 text-basil" />
                    <h2 className="font-playfair text-xl font-semibold text-charcoal">Paket Sipariş</h2>
                  </div>
                  {order.user_phone && <p className="text-charcoal/70">Telefon: <span className="font-medium">{order.user_phone}</span></p>}
                  <p className="text-charcoal/60 text-sm mt-1">Restoran üzerinden teslim alınacak</p>
                </>
              )}
            </motion.div>
          </div>

          {/* Right Column - Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl p-6 shadow-sm sticky top-24">
              <h2 className="font-playfair text-xl font-semibold text-charcoal mb-6">
                Sipariş Özeti
              </h2>
              
              <div className="space-y-4 text-sm">
                <div className="flex justify-between">
                  <span className="text-charcoal/60">Sipariş Tarihi</span>
                  <span className="text-charcoal">
                    {format(new Date(order.created_at), 'd MMMM yyyy, HH:mm', { locale: tr })}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-charcoal/60">Ödeme Yöntemi</span>
                  <span className="text-charcoal">
                    {order.payment_method === 'nakit' ? 'Kapıda Nakit' : 'Kapıda Kredi Kartı'}
                  </span>
                </div>
                {order.notes && (
                  <div className="pt-4 border-t border-stone-200">
                    <span className="text-charcoal/60 block mb-1">Sipariş Notu</span>
                    <span className="text-charcoal">{order.notes}</span>
                  </div>
                )}
              </div>

              <div className="border-t border-stone-200 mt-6 pt-4">
                <div className="flex justify-between">
                  <span className="font-semibold text-charcoal">Toplam</span>
                  <span className="font-playfair text-2xl font-bold text-terracotta">
                    €{order.total_price.toFixed(2)}
                  </span>
                </div>
              </div>

              <div className="mt-6 p-4 bg-sand rounded-xl">
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-charcoal/60" />
                  <div>
                    <p className="text-sm text-charcoal/60">Yardım için arayın</p>
                    <p className="font-semibold text-charcoal">+382 69 144 043</p>
                  </div>
                </div>
              </div>

              {order.status === 'teslim_edildi' && (
                <Button
                  onClick={() => setShowReview(true)}
                  className="w-full mt-4 h-12 rounded-full bg-gradient-to-r from-gold to-amber-500 text-charcoal font-semibold hover:opacity-90"
                  data-testid="open-review-modal-btn"
                >
                  <Star className="w-4 h-4 mr-2 fill-current" />
                  Siparişinizi Değerlendirin
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
      <ReviewModal open={showReview} onClose={() => setShowReview(false)} orderId={orderId} token={token} />
    </div>
  );
};

export default OrderDetail;
